import type { DataPoint, ProviderFile } from "fileParsing/types";
import { compileFn } from "fileParsing/wrangler/compile";
import type { WranglerItem } from "fileParsing/wrangler/types";
import { BaseWorker, EventTypes, type WorkerResponse } from "webworker/BaseWorker";
import { deserializeOutputs } from "@/containers/Visuals/Sankey/parserProcessing";
import { removeDuplicates } from "@/utils/array";
import { WranglerProcessor } from "./WranglerProcessor";

export function pathToFileName(path: string): string {
  return path.slice(path.lastIndexOf("/") + 1);
}

export function pathToFolder(path: string): string {
  return path.indexOf("/") === -1 ? "root" : path.slice(0, path.indexOf("/"));
}

export class WranglerWorker extends BaseWorker<Record<string, never>> {
  providerId: string | undefined;
  files: ProviderFile[] = [];
  data: DataPoint[] = [];

  constructor(identifier: string, data: File | ProviderFile[]) {
    super(identifier);
    if (!Array.isArray(data)) {
      this.parseArchive(data);
    } else {
      this.providerId = "test";
      this.files = data ?? [];
      this.data = this.files
        .reduce<DataPoint[]>((result, file) => result.concat(file.data), [])
        .sort((a, b) => a.date.getTime() - b.date.getTime());
    }
  }

  async parseArchive(zipFile: File) {
    const result = await WranglerProcessor.createFromArchive(zipFile, (progress: number) =>
      this.updateProgress(progress),
    );
    this.files = result.files;
    this.providerId = result.provider.id;
    postMessage({
      type: "event",
      eventType: `${this.identifier}_${EventTypes.DataReady}`,
      data: result.wranglerItems,
    });
    this.data = this.files
      .reduce<DataPoint[]>((result, file) => result.concat(file.data), [])
      .sort((a, b) => a.date.getTime() - b.date.getTime());
  }

  getProviderId() {
    return this.providerId;
  }

  getFiles(item: WranglerItem | undefined = undefined) {
    if (item === undefined) return this.response(this.files);
    return this.response(WranglerProcessor.matchFiles(item, this.files));
  }

  getFile(filepath: string) {
    return this.files.find((file) => file.path === filepath);
  }

  getDataPoints(intervals?: [Date, Date][]) {
    if (intervals === undefined) return this.data;
    const unixIntervals = intervals.map(([a, b]) => [a.getTime(), b.getTime()]);
    return this.data.filter(({ date }) => {
      const time = date.getTime();
      return unixIntervals.some(([a, b]) => a > time && b < time);
    });
  }

  compileNewTemplates(item: WranglerItem) {
    const files = this.getFiles(item);
    const deserializedOutputs = deserializeOutputs(removeDuplicates(files.data.map((file) => file.providerId)));

    if (files.data === undefined) return;

    let hasData = false;

    for (let file of files.data) {
      file = compileFn(item, file, deserializedOutputs);

      hasData = hasData || file.data.length > 0;

      const ix = this.files.findIndex(({ id }) => id === file.id);
      this.files[ix] = file;
      this.data = this.files
        .reduce<DataPoint[]>((result, files) => result.concat(files.data), [])
        .sort((a, b) => a.date.getTime() - b.date.getTime());
    }

    postMessage({
      type: "event",
      eventType: `${this.identifier}_${EventTypes.DataReady}`,
      data: this.files,
    });

    return hasData;
  }

  private updateProgress(percent: number, status?: string) {
    postMessage({
      type: "event",
      eventType: `${this.identifier}_${EventTypes.Progress}`,
      data: percent,
      msg: status,
    });
  }

  private response<T>(data: T): WorkerResponse<T> {
    return {
      eventPrefix: this.identifier,
      data,
    };
  }
}
